import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { VerifyPhoneNumberComponent } from '../../components/verify-phone-number/verify-phone-number.component';
import { AuthService } from '../../../../core/services/auth.service';
import { SwalService } from '../../../../core/services/swal.service';
import { ModalService } from '../../../../core/services/modal.service';
import { LoginModalComponent } from '../../../../shared/components/modal/login-modal/login-modal.component';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { UrlService } from '../../../../core/services/url.service';
import { CountryStateCityService, CountryStateCityCountry, CountryStateCityState, CountryStateCityCity } from '../../../../core/services/country-state-city.service';
import { selectEnableRegistration } from '../../../../store/settings/settings.selectors';
import { environment } from '../../../../../environments';

@Component({
  selector: 'app-registration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BannerComponent,
    VerifyPhoneNumberComponent,
  ],
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
})
export class RegistrationComponent implements OnInit {
  registrationForm: FormGroup;
  loading = false;
  submitted = false;
  error: string | null = null;

  isPublicVersion = environment.theme.default === 'dpp';

  otpId: string | null = null;
  phoneNumber: string | null = null;
  indicative: string | null = null;

  // Propriétés pour la visibilité des mots de passe
  showPassword = false;
  showConfirmPassword = false;

  countries: CountryStateCityCountry[] = [];
  regions: CountryStateCityState[] = [];
  communes: CountryStateCityCity[] = [];

  bannnerLinks = [
    { label: 'Portail Public', href: '/' },
    { label: 'Création Dossier Patient', href: undefined },
  ];

  constructor(
    private fb: FormBuilder,
    private countryStateCityService: CountryStateCityService,
    private authService: AuthService,
    private swalService: SwalService,
    private modalService: ModalService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private store: Store,
    private urlService: UrlService
  ) {
    this.registrationForm = this.fb.group(
      {
        firstname: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
        lastname: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
        email: ['', [this.optionalEmailValidator]], // Optionnel selon le DTO
        password: ['', [Validators.minLength(8), Validators.maxLength(30)]], // Optionnel selon le DTO
        confirmPassword: ['', Validators.required],
        sex: ['', Validators.required],
        birthday: ['', Validators.required],
        nationality: ['', Validators.required],
        address: ['', Validators.required],
        country: ['', Validators.required],
        region: ['', Validators.required],
        commune: ['', Validators.required],
        source: ['DPUP', Validators.required],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.store.select(selectEnableRegistration).pipe(take(1)).subscribe((enableRegistration) => {
      if (!enableRegistration) {
        this.router.navigate(this.urlService.buildRouteSegments('403'));
        return;
      }

      this.countryStateCityService.getCountries().subscribe({
        next: (countries) => {
          this.countries = countries;
        },
        error: () => {
          this.error = 'Impossible de charger la liste des pays.';
        },
      });

      this.registrationForm
        .get('country')
        ?.valueChanges.subscribe((countryIso2) => {
          if (countryIso2) {
            this.onCountryChange(countryIso2);
          } else {
            this.regions = [];
            this.communes = [];
            this.registrationForm.patchValue({ region: '', commune: '' });
          }
        });

      this.registrationForm
        .get('region')
        ?.valueChanges.subscribe((stateIso2) => {
          if (stateIso2) {
            const countryIso2 = this.registrationForm.get('country')?.value;
            if (countryIso2) {
              this.onRegionChange(countryIso2, stateIso2);
            }
          } else {
            this.communes = [];
            this.registrationForm.patchValue({ commune: '' });
          }
        });
    });
  }

  onCountryChange(countryIso2: string): void {
    this.countryStateCityService.getStatesByCountry(countryIso2).subscribe({
      next: (states) => {
        this.regions = states;
        this.registrationForm.patchValue({ region: '', commune: '' });
        this.communes = [];
      },
      error: () => {
        this.regions = [];
        this.communes = [];
      },
    });
  }

  onRegionChange(countryIso2: string, stateIso2: string): void {
    this.countryStateCityService.getCitiesByState(countryIso2, stateIso2).subscribe({
      next: (cities) => {
        this.communes = cities;
        this.registrationForm.patchValue({ commune: '' });
      },
      error: () => {
        this.communes = [];
      },
    });
  }

  passwordMatchValidator(form: FormGroup) {
    return form.get('password')!.value === form.get('confirmPassword')!.value
      ? null
      : { mismatch: true };
  }

  // Validateur pour email optionnel : accepte une chaîne vide ou un email valide
  optionalEmailValidator(control: FormControl) {
    if (!control.value || control.value.trim() === '') {
      return null; // Email vide est valide (optionnel)
    }
    // Si une valeur est fournie, valider le format de l'email
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(control.value) ? null : { email: true };
  }

  // Méthodes pour basculer la visibilité des mots de passe
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onOtpRequested(otpData: any) {
    this.otpId = otpData.otpId;
    this.phoneNumber = otpData.phoneNumber;
    this.indicative = otpData.indicative;
  }

  getNationalityName(code: string): string {
    const nationalities: { [key: string]: string } = {
      'SN': 'Sénégalaise'
    };
    return nationalities[code] || code;
  }

  // Méthodes helper pour vérifier les erreurs de validation
  hasError(fieldName: string): boolean {
    const field = this.registrationForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty || this.submitted));
  }

  getErrorMessage(fieldName: string): string {
    const field = this.registrationForm.get(fieldName);
    if (!field || (!field.errors && !this.registrationForm.errors)) {
      return '';
    }

    // Erreurs au niveau du champ
    if (field.errors) {
      if (field.errors['required']) {
        return 'Ce champ est obligatoire';
      }
      if (field.errors['minlength']) {
        return `Minimum ${field.errors['minlength'].requiredLength} caractères requis`;
      }
      if (field.errors['maxlength']) {
        return `Maximum ${field.errors['maxlength'].requiredLength} caractères autorisés`;
      }
      if (field.errors['email']) {
        return 'Format d\'email invalide';
      }
    }

    // Erreurs au niveau du formulaire (pour confirmPassword)
    if (fieldName === 'confirmPassword' && this.registrationForm.errors?.['mismatch']) {
      return 'Les mots de passe ne correspondent pas';
    }

    return '';
  }

  getFieldClass(fieldName: string): string {
    const baseClass = 'input w-full mt-2 placeholder:text-gray-400 text-gray-600';
    if (this.hasError(fieldName)) {
      return `${baseClass} border-red-500 focus:border-red-500 focus:ring-red-500`;
    }
    return baseClass;
  }

  getSelectClass(fieldName: string): string {
    const baseClass = 'select text-gray-700 w-full mt-2';
    if (this.hasError(fieldName)) {
      return `${baseClass} border-red-500 focus:border-red-500 focus:ring-red-500`;
    }
    return baseClass;
  }

  onSubmit() {
    this.submitted = true;
    this.error = null;

    // Marquer tous les champs comme touched pour afficher les erreurs
    Object.keys(this.registrationForm.controls).forEach(key => {
      this.registrationForm.get(key)?.markAsTouched();
    });

    console.log('Registration form:', this.registrationForm.value);
    console.log('Form errors:', this.registrationForm.errors);
    console.log('Form valid:', this.registrationForm.valid);
    
    // Afficher les erreurs de chaque champ dans la console pour debug
    Object.keys(this.registrationForm.controls).forEach(key => {
      const control = this.registrationForm.get(key);
      if (control && control.invalid) {
        console.log(`Field ${key} errors:`, control.errors);
      }
    });

    if (this.registrationForm.invalid) {
      // Faire défiler vers le premier champ en erreur
      const firstErrorField = Object.keys(this.registrationForm.controls).find(key => {
        const control = this.registrationForm.get(key);
        return control && control.invalid;
      });
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    if (!this.otpId) {
      this.error = "Veuillez d'abord vérifier votre numéro de téléphone.";
      console.log('Error:', this.error);
      return;
    }

    this.loading = true;

    // Formater la date de naissance au format DD/MM/YYYY
    const formatBirthday = (dateString: string): string => {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Préparer les données pour l'inscription selon le format attendu par l'API
    const formValue = this.registrationForm.value;
    const countryIso2 = formValue.country;
    const stateIso2 = formValue.region;
    const countryName = this.countries.find((c) => c.iso2 === countryIso2)?.name ?? countryIso2;
    const regionName = this.regions.find((s) => s.iso2 === stateIso2)?.name ?? stateIso2;

    const registrationData = {
      firstName: formValue.firstname,
      lastName: formValue.lastname,
      email: formValue.email || undefined,
      phoneNumber: this.phoneNumber!,
      indicative: this.indicative || '',
      sex: formValue.sex,
      birthday: formatBirthday(formValue.birthday),
      nationality: {
        code: formValue.nationality,
        label: this.getNationalityName(formValue.nationality)
      },
      address: {
        country: {
          code: countryIso2,
          label: countryName
        },
        region: regionName,
        departement: regionName,
        commune: formValue.commune,
        street: formValue.address
      },
      accountType: 'MAIN' as const,
      source: formValue.source,
      otpId: this.otpId,
      password: formValue.password || undefined,
    };

    console.log('Submitting registration with data:', registrationData);

    this.authService.register(registrationData).subscribe({
      next: (response) => {
        console.log('Registration response:', response);
        this.loading = false;
        
        // Tracker l'inscription réussie avec Firebase Analytics
        this.analyticsService.logEvent('registration_success', {
          source: registrationData.source,
          hasEmail: !!registrationData.email,
          hasPassword: !!registrationData.password,
        });
        
        const alertTitle = this.isPublicVersion ? 'Pré-enrôlement réussi !' : 'Inscription réussie !';
        // Afficher le SweetAlert de succès
        this.swalService.success(
          alertTitle,
          'Votre dossier patient a été créé avec succès. Vous pouvez maintenant vous connecter.'
        ).then(() => {
          // Ouvrir le modal de connexion
          this.otpId = null;
          this.registrationForm.reset();
          this.modalService.open({
            component: LoginModalComponent,
            width: '500px',
            title: 'Connexion',
            closable: true,
          });
        });
      },
      complete: () => {
        this.loading = false;
      },
      error: (err) => {
        console.error('Registration error:', err);
        this.loading = false;
        
        // Gérer les erreurs spécifiques de l'API
        let errorMessage = 'Erreur lors de l\'inscription. Veuillez réessayer.';
        if (err.error && err.error.message) {
          if (Array.isArray(err.error.message)) {
            errorMessage = err.error.message.join(', ');
          } else {
            errorMessage = err.error.message;
          }
        } else if (err.error && err.error.error) {
          errorMessage = err.error.error;
        }
        this.error = errorMessage;
        
        // Tracker l'échec d'inscription avec Firebase Analytics
        this.analyticsService.logEvent('registration_failure', {
          error_message: errorMessage,
          source: registrationData.source,
        });
      },
    });
  }
}
